import { MoveLeft, MoveRight } from 'lucide-react';
import React from 'react';
import { Button } from '@/code/frontend/components/ui/button';

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pageNumbers.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(
          1,
          '...',
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages,
        );
      } else {
        pageNumbers.push(
          1,
          '...',
          currentPage - 1,
          currentPage,
          currentPage + 1,
          '...',
          totalPages,
        );
      }
    }
    return pageNumbers;
  };

  if (totalItems <= itemsPerPage) {
    return null;
  }

  return (
    <div className="flex items-center justify-between p-4">
      <div className="w-[7.5rem]">
        {currentPage !== 1 && (
          <Button
            onClick={handlePrevious}
            disabled={currentPage === 1}
            variant="airfoil-secondary"
            size="sm"
            iconLeft={<MoveLeft />}
          >
            Previous
          </Button>
        )}
      </div>
      <div className="flex items-center gap-2">
        {getPageNumbers()
          .map((page, i) => ({
            page,
            id: typeof page === 'number' ? `page-${page}` : `ellipsis-${i}`,
          }))
          .map(({ page, id }) =>
            typeof page === 'number' ? (
              <Button
                key={id}
                onClick={() => onPageChange(page)}
                variant={
                  currentPage === page ? 'airfoil-secondary' : 'airfoil-ghost'
                }
                size="airfoil-icon"
              >
                {page}
              </Button>
            ) : (
              <span key={id} className="px-2 py-1 text-sm">
                {page}
              </span>
            ),
          )}
      </div>
      <div className="w-[7.5rem]">
        {currentPage !== totalPages && (
          <Button
            onClick={handleNext}
            disabled={currentPage === totalPages}
            variant="airfoil-secondary"
            size="sm"
            iconRight={<MoveRight />}
          >
            Next
          </Button>
        )}
      </div>
    </div>
  );
};
